/**
 * Custom Cart Drawer — JavaScript
 *
 * Features:
 * 1. Progress bar — dynamic fill + label with converted thresholds
 * 2. Auto-gift management — adds/removes gift products with converted thresholds
 * 3. Quantity selects — intercepts change events for Dawn compatibility
 * 4. Upsell quick-add — multiple upsell buttons
 * 5. Gift image modal — lazy-loaded gallery
 * 6. Real-time synchronization via Dawn's pub/sub events
 */

class CustomCartDrawerEnhancements {
  constructor() {
    this._giftLock = false;
    this._reinitTimer = null;
    this._cartUpdateUnsubscriber = null;
    this.init();
    this.observeChanges();
    this.subscribeToCartUpdates();
  }

  init() {
    this.initQuantitySelects();
    this.initProgressBar();
    this.initUpsellButtons();
    this.initGiftGallery();
  }

  /* ========================================================================
     CART UPDATE SUBSCRIPTION
     ======================================================================== */

  subscribeToCartUpdates() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'custom-cart-drawer-gift') return;
        clearTimeout(this._reinitTimer);
        this._reinitTimer = setTimeout(() => this.init(), 200);
      });
    }
  }

  /* ========================================================================
     QUANTITY SELECTS
     ======================================================================== */

  initQuantitySelects() {
    const selects = document.querySelectorAll('.custom-cart-drawer__qty-select');
    selects.forEach(select => {
      if (select.dataset.customBound === 'true') return;
      select.dataset.customBound = 'true';

      select.addEventListener('change', (event) => {
        event.stopPropagation();
        const index = parseInt(event.target.dataset.index, 10);
        const quantity = parseInt(event.target.value, 10);
        const variantId = event.target.dataset.quantityVariantId;
        const cartItems = event.target.closest('cart-drawer-items');

        if (cartItems && typeof cartItems.updateQuantity === 'function') {
          cartItems.updateQuantity(index, quantity, event, event.target.name, variantId);
        }
      });
    });
  }

  /* ========================================================================
     PROGRESS BAR — with currency conversion + tolerance support
     ======================================================================== */

  initProgressBar() {
    const progressEl = document.querySelector('.custom-cart-drawer__progress');
    if (!progressEl) return;

    const cartTotal = parseInt(progressEl.dataset.cartTotal, 10) || 0;
    const tolerance = parseInt(progressEl.dataset.tolerance, 10) || 0;
    let steps;

    try {
      steps = JSON.parse(progressEl.dataset.steps);
    } catch (e) {
      console.warn('[CustomCartDrawer] Invalid progress steps JSON:', e);
      return;
    }

    if (!steps || steps.length === 0) return;

    const currencySymbol = progressEl.dataset.currencySymbol || '€';
    const baseCurrency = progressEl.dataset.baseCurrency || 'EUR';
    const presentmentCurrency = progressEl.dataset.presentmentCurrency || baseCurrency;
    const messageBefore = progressEl.dataset.messageBefore || 'Plus que';
    const messageAfter = progressEl.dataset.messageAfter || 'pour obtenir un(e)';
    const messageComplete = progressEl.dataset.messageComplete || 'Félicitations ! Vous avez débloqué :';
    const messageTolerance = progressEl.dataset.messageTolerance || 'On est sympa, c\'est cadeau 😉';

    const convertedProgress = this.convertProgressSteps(steps, tolerance, baseCurrency, presentmentCurrency);
    steps = convertedProgress.steps;
    const presentmentTolerance = convertedProgress.tolerance;

    steps.sort((a, b) => a.threshold - b.threshold);

    const allGroups = progressEl.querySelectorAll('.custom-cart-drawer__progress-group');
    const stepElements = Array.from(allGroups).filter(
      el => el.querySelector('.custom-cart-drawer__progress-group-icons')
    );
    const spacerEl = Array.from(allGroups).find(
      el => !el.querySelector('.custom-cart-drawer__progress-group-icons')
    );

    let nextUncompletedStep = null;
    let allCompleted = true;
    let toleranceTriggered = false;
    let toleranceStep = null;

    stepElements.forEach((groupEl, index) => {
      const step = steps[index];
      if (!step) return;

      const threshold = step.threshold;
      const effectiveThreshold = threshold - presentmentTolerance;
      const prevThreshold = index > 0 ? steps[index - 1].threshold : 0;
      const rangeSize = threshold - prevThreshold;

      let fillPercent = 0;

      if (cartTotal >= effectiveThreshold) {
        fillPercent = 100;
        groupEl.classList.add('custom-cart-drawer__progress-group--completed');

        // Check if this was a tolerance-triggered completion
        if (cartTotal < threshold && cartTotal >= effectiveThreshold) {
          toleranceTriggered = true;
          toleranceStep = step;
        }
      } else if (cartTotal > prevThreshold && rangeSize > 0) {
        fillPercent = ((cartTotal - prevThreshold) / rangeSize) * 100;
        fillPercent = Math.min(fillPercent, 100);
        groupEl.classList.remove('custom-cart-drawer__progress-group--completed');

        if (!nextUncompletedStep) {
          nextUncompletedStep = step;
          allCompleted = false;
        }
      } else {
        fillPercent = 0;
        groupEl.classList.remove('custom-cart-drawer__progress-group--completed');

        if (!nextUncompletedStep) {
          nextUncompletedStep = step;
          allCompleted = false;
        }
      }

      const progressBar = groupEl.querySelector('.custom-cart-drawer__progress-element');
      if (progressBar) {
        progressBar.value = fillPercent;
      }

      const thresholdLabel = groupEl.querySelector('.custom-cart-drawer__step-threshold');
      if (thresholdLabel) {
        thresholdLabel.textContent = this.formatMoney(threshold, {
          currency: presentmentCurrency,
          symbol: currencySymbol,
        });
      }
    });

    // Fill spacer when all completed
    if (spacerEl) {
      const spacerProgress = spacerEl.querySelector('.custom-cart-drawer__progress-element');
      if (allCompleted) {
        spacerEl.classList.add('custom-cart-drawer__progress-group--completed');
        if (spacerProgress) spacerProgress.value = 100;
      } else {
        spacerEl.classList.remove('custom-cart-drawer__progress-group--completed');
        if (spacerProgress) spacerProgress.value = 0;
      }
    }

    // Update label text
    const labelEl = progressEl.querySelector('.custom-cart-drawer__progress-label');
    if (labelEl) {
      if (toleranceTriggered && toleranceStep) {
        // Tolerance message — within the configured buffer of a threshold
        labelEl.innerHTML = `<span>${messageTolerance} <b>${toleranceStep.reward}</b></span>`;
      } else if (allCompleted || !nextUncompletedStep) {
        const lastReward = steps[steps.length - 1].reward;
        labelEl.innerHTML = `<span>${messageComplete} <b>${lastReward}</b></span>`;
      } else {
        const missing = nextUncompletedStep.threshold - cartTotal;
        const missingFormatted = this.formatMoney(missing, {
          currency: presentmentCurrency,
          symbol: currencySymbol,
        });
        labelEl.innerHTML = `<span>${messageBefore} <span class="missing-value">${missingFormatted}</span> ${messageAfter} <b>${nextUncompletedStep.reward}</b></span>`;
      }
    }

    // Auto-manage gifts (with converted thresholds + tolerance)
    this.autoManageGifts(steps, cartTotal, presentmentTolerance, convertedProgress.canQualify);
  }

  convertProgressSteps(steps, tolerance, baseCurrency, presentmentCurrency) {
    const rate = this.getPresentmentCurrencyRate(baseCurrency, presentmentCurrency);
    const canConvert = Number.isFinite(rate) && rate > 0;
    const conversionRate = canConvert ? rate : 1;

    if (!canConvert && baseCurrency !== presentmentCurrency) {
      console.warn('[CustomCartDrawer] Currency rate unavailable. Gift thresholds will not qualify in this currency.');
    }

    return {
      canQualify: canConvert,
      tolerance: this.convertCentsToPresentment(tolerance, conversionRate),
      steps: steps.map(step => {
        const baseThreshold = parseInt(step.threshold, 10) || 0;
        const convertedThreshold = this.convertCentsToPresentment(baseThreshold, conversionRate);

        return {
          ...step,
          baseThreshold,
          threshold: this.roundThresholdToNearestTen(convertedThreshold),
        };
      }),
    };
  }

  getPresentmentCurrencyRate(baseCurrency, presentmentCurrency) {
    if (!baseCurrency || !presentmentCurrency || baseCurrency === presentmentCurrency) return 1;

    const shopifyCurrency = window.Shopify && window.Shopify.currency;
    const activeCurrency = shopifyCurrency && shopifyCurrency.active;
    const rate = shopifyCurrency ? parseFloat(shopifyCurrency.rate) : NaN;

    if (
      Number.isFinite(rate) &&
      rate > 0 &&
      (!activeCurrency || activeCurrency === presentmentCurrency)
    ) {
      return rate;
    }

    return null;
  }

  convertCentsToPresentment(cents, rate) {
    return Math.max(0, Math.round((parseInt(cents, 10) || 0) * rate));
  }

  roundThresholdToNearestTen(cents) {
    const majorUnits = cents / 100;
    const roundedMajorUnits = Math.round(majorUnits / 10) * 10;
    return Math.max(0, Math.round(roundedMajorUnits * 100));
  }

  formatMoney(cents, options = {}) {
    const symbol = typeof options === 'string' ? options : options.symbol;
    const currency = typeof options === 'object' ? options.currency : null;
    const normalizedCents = Math.max(0, Math.round(cents));
    const amount = normalizedCents / 100;
    const hasCents = normalizedCents % 100 !== 0;

    if (currency && typeof Intl !== 'undefined' && typeof Intl.NumberFormat === 'function') {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'fr-FR', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: hasCents ? 2 : 0,
          maximumFractionDigits: hasCents ? 2 : 0,
        }).format(amount);
      } catch (error) {
        console.warn('[CustomCartDrawer] Money formatting failed:', error);
      }
    }

    const formatted = amount.toFixed(hasCents ? 2 : 0).replace('.', ',');
    return `${formatted}${symbol || ''}`;
  }

  /* ========================================================================
     AUTO-GIFT MANAGEMENT — with converted tolerance
     ======================================================================== */

  async autoManageGifts(steps, qualifyingTotal, tolerance, canQualify = true) {
    if (this._giftLock) return;

    const giftSteps = steps.filter(s => s.giftVariantId);
    if (giftSteps.length === 0) return;

    this._giftLock = true;

    try {
      const cartResponse = await fetch(`${window.routes.cart_url}.js`, {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await cartResponse.json();

      const freshQualifyingTotal = cart.items
        .filter(item => !(item.properties && item.properties._is_gift === 'true'))
        .reduce((sum, item) => sum + item.final_line_price, 0);

      const giftsToAdd = [];
      const giftsToRemove = [];

      for (const step of giftSteps) {
        const effectiveThreshold = step.threshold - tolerance;
        const isQualified = canQualify && freshQualifyingTotal >= effectiveThreshold;

        const giftInCart = cart.items.find(
          item => item.variant_id === step.giftVariantId &&
                  item.properties &&
                  item.properties._is_gift === 'true'
        );

        if (isQualified && !giftInCart) {
          giftsToAdd.push(step.giftVariantId);
        } else if (!isQualified && giftInCart) {
          giftsToRemove.push(giftInCart.key);
        }
      }

      if (giftsToAdd.length === 0 && giftsToRemove.length === 0) return;

      let cartChanged = false;

      if (giftsToAdd.length > 0) {
        const items = giftsToAdd.map(variantId => ({
          id: variantId,
          quantity: 1,
          properties: { '_is_gift': 'true' }
        }));

        const addResponse = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (addResponse.ok) cartChanged = true;
      }

      if (giftsToRemove.length > 0) {
        const updates = {};
        giftsToRemove.forEach(key => { updates[key] = 0; });

        const removeResponse = await fetch(window.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates })
        });

        if (removeResponse.ok) cartChanged = true;
      }

      if (cartChanged) {
        await this.refreshDrawer();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-cart-drawer-gift',
            cartData: {},
          });
        }
      }

    } catch (error) {
      console.error('[CustomCartDrawer] Auto-gift management failed:', error);
    } finally {
      setTimeout(() => { this._giftLock = false; }, 500);
    }
  }

  async refreshDrawer() {
    try {
      const [drawerRes, bubbleRes] = await Promise.all([
        fetch(`${window.routes.cart_url}?section_id=cart-drawer`),
        fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
      ]);

      const drawerHtml = await drawerRes.text();
      const bubbleHtml = await bubbleRes.text();

      const drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const newDrawerInner = drawerDoc.querySelector('.drawer__inner');
      const currentDrawerInner = document.querySelector('#CartDrawer .drawer__inner');

      if (newDrawerInner && currentDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }

      const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const newBubble = bubbleDoc.querySelector('.shopify-section');
      const currentBubble = document.getElementById('cart-icon-bubble');

      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawer) {
        const items = cartDrawerItems
          ? cartDrawerItems.querySelectorAll('.custom-cart-drawer__item')
          : [];
        cartDrawer.classList.toggle('is-empty', items.length === 0);
      }
    } catch (error) {
      console.error('[CustomCartDrawer] Drawer refresh failed:', error);
    }
  }

  /* ========================================================================
     UPSELL QUICK-ADD (multiple buttons)
     ======================================================================== */

  initUpsellButtons() {
    const buttons = document.querySelectorAll('.custom-cart-drawer__upsell-add');
    buttons.forEach(btn => {
      if (btn.dataset.customBound === 'true') return;
      btn.dataset.customBound = 'true';

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleUpsellAdd(btn);
      });
    });

    /* Bind variant selects — update button's data-variant-id on change */
    const selects = document.querySelectorAll('.custom-cart-drawer__upsell-variant-select');
    selects.forEach(select => {
      if (select.dataset.customBound === 'true') return;
      select.dataset.customBound = 'true';

      const card = select.closest('.custom-cart-drawer__upsell-card');
      const addBtn = card ? card.querySelector('.custom-cart-drawer__upsell-add') : null;
      if (addBtn) {
        /* Set initial value from first selected option */
        addBtn.dataset.variantId = select.value;
        select.addEventListener('change', () => {
          addBtn.dataset.variantId = select.value;
        });
      }
    });
  }

  async handleUpsellAdd(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('loading');
    btn.disabled = true;

    try {
      const addResponse = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });

      if (!addResponse.ok) throw new Error('Failed to add to cart');

      const addData = await addResponse.json();

      if (addData.sections && addData.sections['cart-drawer']) {
        const html = new DOMParser().parseFromString(addData.sections['cart-drawer'], 'text/html');
        const newDrawerInner = html.querySelector('.drawer__inner');
        const currentDrawerInner = document.querySelector('#CartDrawer .drawer__inner');
        if (newDrawerInner && currentDrawerInner) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }
      }

      if (addData.sections && addData.sections['cart-icon-bubble']) {
        const bubbleHtml = new DOMParser().parseFromString(addData.sections['cart-icon-bubble'], 'text/html');
        const newBubble = bubbleHtml.querySelector('.shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.classList.remove('is-empty');

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'custom-cart-drawer',
          cartData: addData,
        });
      }

    } catch (error) {
      console.error('[CustomCartDrawer] Upsell add failed:', error);
    } finally {
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  }

  /* ========================================================================
     GIFT IMAGE GALLERY — lazy-loaded modal
     ======================================================================== */

  initGiftGallery() {
    // Bind the + button on gift images
    const galleryBtns = document.querySelectorAll('.custom-cart-drawer__gift-gallery-btn');
    galleryBtns.forEach(btn => {
      if (btn.dataset.customBound === 'true') return;
      btn.dataset.customBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const imagesRaw = btn.dataset.productImages;
        this.openGiftModal(imagesRaw);
      });
    });

    // Bind the "voir en images" text link
    const galleryLinks = document.querySelectorAll('.custom-cart-drawer__gift-gallery-link');
    galleryLinks.forEach(link => {
      if (link.dataset.customBound === 'true') return;
      link.dataset.customBound = 'true';
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const imagesRaw = link.dataset.productImages;
        this.openGiftModal(imagesRaw);
      });
    });

    // Bind modal close + arrows
    const modal = document.getElementById('CartDrawer-GiftModal');
    if (modal && !modal.dataset.customBound) {
      modal.dataset.customBound = 'true';

      const closeBtn = modal.querySelector('.custom-cart-drawer__modal-close');
      const overlay = modal.querySelector('.custom-cart-drawer__modal-overlay');
      const prevBtn = modal.querySelector('.custom-cart-drawer__modal-arrow--prev');
      const nextBtn = modal.querySelector('.custom-cart-drawer__modal-arrow--next');

      if (closeBtn) closeBtn.addEventListener('click', () => this.closeGiftModal());
      if (overlay) overlay.addEventListener('click', () => this.closeGiftModal());

      if (prevBtn) prevBtn.addEventListener('click', () => this.scrollGallery(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.scrollGallery(1));

      document.addEventListener('keydown', (e) => {
        if (modal.getAttribute('aria-hidden') !== 'false') return;
        if (e.key === 'Escape') this.closeGiftModal();
        if (e.key === 'ArrowLeft') this.scrollGallery(-1);
        if (e.key === 'ArrowRight') this.scrollGallery(1);
      });

      // Update arrow visibility on scroll
      const gallery = modal.querySelector('.custom-cart-drawer__modal-gallery');
      if (gallery) {
        gallery.addEventListener('scroll', () => this.updateArrowVisibility());
      }
    }
  }

  openGiftModal(imagesRaw) {
    const modal = document.getElementById('CartDrawer-GiftModal');
    if (!modal) return;

    const gallery = modal.querySelector('.custom-cart-drawer__modal-gallery');
    const content = modal.querySelector('.custom-cart-drawer__modal-content');

    // Clear previous images
    gallery.innerHTML = '';

    // Parse images
    let images = [];
    try {
      if (imagesRaw.startsWith('[')) {
        images = JSON.parse(imagesRaw);
      } else {
        images = JSON.parse('[' + imagesRaw + ']');
      }
    } catch (e) {
      console.warn('[CustomCartDrawer] Could not parse gallery images:', e);
      return;
    }

    if (images.length === 0) return;

    // Show modal + loading
    modal.setAttribute('aria-hidden', 'false');
    content.classList.add('custom-cart-drawer__modal--loading');

    // Create images lazily
    let loadedCount = 0;

    images.forEach(src => {
      const img = document.createElement('img');
      img.alt = '';
      img.addEventListener('load', () => {
        loadedCount++;
        if (loadedCount >= 1) {
          content.classList.remove('custom-cart-drawer__modal--loading');
          this.updateArrowVisibility();
        }
      });
      img.src = src;
      gallery.appendChild(img);
    });

    // Reset scroll position
    gallery.scrollLeft = 0;
    this.updateArrowVisibility();
  }

  scrollGallery(direction) {
    const modal = document.getElementById('CartDrawer-GiftModal');
    if (!modal) return;

    const gallery = modal.querySelector('.custom-cart-drawer__modal-gallery');
    if (!gallery) return;

    const slideWidth = gallery.offsetWidth;
    gallery.scrollBy({ left: direction * slideWidth, behavior: 'smooth' });
  }

  updateArrowVisibility() {
    const modal = document.getElementById('CartDrawer-GiftModal');
    if (!modal) return;

    const gallery = modal.querySelector('.custom-cart-drawer__modal-gallery');
    const prevBtn = modal.querySelector('.custom-cart-drawer__modal-arrow--prev');
    const nextBtn = modal.querySelector('.custom-cart-drawer__modal-arrow--next');

    if (!gallery || !prevBtn || !nextBtn) return;

    const atStart = gallery.scrollLeft <= 2;
    const atEnd = gallery.scrollLeft + gallery.offsetWidth >= gallery.scrollWidth - 2;
    const totalImages = gallery.querySelectorAll('img').length;

    if (totalImages <= 1) {
      prevBtn.classList.add('hidden');
      nextBtn.classList.add('hidden');
    } else {
      prevBtn.classList.toggle('hidden', atStart);
      nextBtn.classList.toggle('hidden', atEnd);
    }
  }

  closeGiftModal() {
    const modal = document.getElementById('CartDrawer-GiftModal');
    if (modal) {
      modal.setAttribute('aria-hidden', 'true');
      const content = modal.querySelector('.custom-cart-drawer__modal-content');
      if (content) content.classList.remove('custom-cart-drawer__modal--loading');
    }
  }

  /* ========================================================================
     MUTATION OBSERVER
     ======================================================================== */

  observeChanges() {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    const observer = new MutationObserver(() => {
      clearTimeout(this._reinitTimer);
      this._reinitTimer = setTimeout(() => this.init(), 150);
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }
}

/* --------------------------------------------------------------------------
   Initialize when DOM is ready
   -------------------------------------------------------------------------- */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CustomCartDrawerEnhancements());
} else {
  new CustomCartDrawerEnhancements();
}
